// Card "Limite WhatsApp" da prévia da lista de campanha. Vive fora do
// TablePreviewModal pra ser testável: a regra que importa é o que a prévia
// AFIRMA sobre quantos contatos vão sair. Antes ela morava no .vue e qualquer
// limite que não chegasse (busca falhou, caixa não resolvida) virava "Ilimitado"
// verde num canal travado em 1.000 (caso Lumen, 2026-10-21).
//
// Fontes do `whatsappLimit` (ver `useDataFetching#fetchWhatsappLimit`):
// - endpoint `whatsapp_message_limit` (caixa oficial): sempre traz a chave
//   `limit_checked_at`; nula = tier nunca lido na Meta, o `limit` não é real.
//   `limit` é número ou 'unlimited'; `remaining` pode ser NEGATIVO (tier
//   rebaixado depois de o dia já ter enviado mais que o teto novo).
//   `meta_unlimited: true` = a Meta respondeu TIER_UNLIMITED, mas o Cortex
//   segue cortando em `limit` (o motor de campanha ainda não dispara sem teto,
//   ver Whatsapp::MessagingTier#daily_limit_update). O corte real é o do
//   Cortex, então a conta de quantos saem usa o `remaining` dele.
// - store `campaigns/getMessageLimit` (Baileys/não oficial): sem
//   `limit_checked_at`; sem `limit` = sem teto (comportamento antigo).
// - `error` preenchido = a leitura falhou; nada sobre o limite é afirmado.
// - `loading` = a leitura ainda não voltou; `{}` aí não quer dizer "sem teto".

export const PREVIEW_LIMIT_STATUS = {
  // Dentro do que sobra do dia.
  OK: 'ok',
  // A lista passa do que sobra: só parte dela sai.
  EXCEEDS: 'exceeds',
  // Nada sobra hoje: nenhum envio.
  REACHED: 'reached',
  // Tier da Meta sem teto diário.
  UNLIMITED: 'unlimited',
  // Meta libera ilimitado, o Cortex corta em `limit` (mesmo nome em
  // home/campaignQuota.js).
  CAPPED: 'capped',
  // Tier nunca lido na Meta.
  UNCHECKED: 'unchecked',
  // Leitura falhou.
  ERROR: 'error',
  // Leitura em andamento.
  LOADING: 'loading',
  // Fonte não oficial sem limite definido (Baileys).
  NONE: 'none',
};

const QUALITY_INK = {
  GREEN: { bg: 'bg-n-green-3', text: 'text-n-green-11', icon: '🟢' },
  YELLOW: { bg: 'bg-n-amber-3', text: 'text-n-amber-11', icon: '🟡' },
  RED: { bg: 'bg-n-ruby-3', text: 'text-n-ruby-11', icon: '🔴' },
  UNKNOWN: { bg: 'bg-n-slate-3', text: 'text-n-slate-11', icon: '⚪' },
};

const WARN_INK = { bg: 'bg-n-amber-3', text: 'text-n-amber-11', icon: '⚠️' };
const ERROR_INK = { bg: 'bg-n-ruby-3', text: 'text-n-ruby-11', icon: '⚠️' };

// Estados em que a prévia conhece o teto do dia e conta quantos saem.
const COUNTED = [PREVIEW_LIMIT_STATUS.OK, PREVIEW_LIMIT_STATUS.CAPPED];

function limitStatus(whatsappLimit, error, loading) {
  if (error) return PREVIEW_LIMIT_STATUS.ERROR;
  if (loading) return PREVIEW_LIMIT_STATUS.LOADING;
  const fromEndpoint = 'limit_checked_at' in whatsappLimit;
  if (fromEndpoint && !whatsappLimit.limit_checked_at) {
    return PREVIEW_LIMIT_STATUS.UNCHECKED;
  }
  if (whatsappLimit.limit === 'unlimited') {
    return PREVIEW_LIMIT_STATUS.UNLIMITED;
  }
  if (fromEndpoint) {
    // Conferido, mas sem número: o motor de campanha não roda com isso
    // (`daily_limit - send_count`). Não é "sem limite" — é leitura inválida.
    if (typeof whatsappLimit.limit !== 'number') {
      return PREVIEW_LIMIT_STATUS.ERROR;
    }
    if (whatsappLimit.meta_unlimited === true) {
      return PREVIEW_LIMIT_STATUS.CAPPED;
    }
    return PREVIEW_LIMIT_STATUS.OK;
  }
  if (!(typeof whatsappLimit.limit === 'number' && whatsappLimit.limit > 0)) {
    return PREVIEW_LIMIT_STATUS.NONE;
  }
  return PREVIEW_LIMIT_STATUS.OK;
}

// O que cabe agora é o que SOBRA do teto do dia, nunca menos que zero.
// Sem `remaining` (store antigo), o teto inteiro.
function sendableToday(whatsappLimit) {
  const { limit, remaining } = whatsappLimit;
  if (typeof remaining === 'number') return Math.max(0, remaining);
  return limit;
}

export function buildPreviewLimit({
  whatsappLimit = {},
  totalCount = 0,
  error = null,
  loading = false,
} = {}) {
  const source = whatsappLimit || {};
  const qualityRating = source.quality_rating || 'UNKNOWN';
  let status = limitStatus(source, error, loading);

  if (!COUNTED.includes(status)) {
    return {
      status,
      limit: null,
      remainingToday: null,
      recordsToSend: null,
      cut: false,
      qualityRating,
    };
  }

  const sendable = sendableToday(source);
  // A lista passa do que sobra hoje: só parte dela sai.
  const cut = totalCount > sendable;
  if (cut && status === PREVIEW_LIMIT_STATUS.OK) {
    status =
      sendable === 0
        ? PREVIEW_LIMIT_STATUS.REACHED
        : PREVIEW_LIMIT_STATUS.EXCEEDS;
  }

  return {
    status,
    limit: source.limit,
    remainingToday: sendable,
    recordsToSend: Math.min(totalCount, sendable),
    cut,
    qualityRating,
  };
}

// Quanto do corte a lista sofre — mesma frase em OK (EXCEEDS/REACHED) e CAPPED.
function cutText(preview, t) {
  if (preview.recordsToSend === 0) {
    return t('CAMPAIGN.WHATSAPP_OFFICIAL.PREVIEW_LIMIT.REACHED');
  }
  return t('CAMPAIGN.WHATSAPP_OFFICIAL.PREVIEW_LIMIT.EXCEEDS', {
    count: preview.recordsToSend.toLocaleString('pt-BR'),
  });
}

function cappedText(preview, t) {
  return t('CAMPAIGN.WHATSAPP_OFFICIAL.PREVIEW_LIMIT.CAPPED', {
    limit: preview.limit.toLocaleString('pt-BR'),
  });
}

function cardInk(preview) {
  switch (preview.status) {
    case PREVIEW_LIMIT_STATUS.ERROR:
      return ERROR_INK;
    case PREVIEW_LIMIT_STATUS.UNCHECKED:
    case PREVIEW_LIMIT_STATUS.CAPPED:
    case PREVIEW_LIMIT_STATUS.EXCEEDS:
    case PREVIEW_LIMIT_STATUS.REACHED:
      return WARN_INK;
    default:
      return QUALITY_INK[preview.qualityRating] || QUALITY_INK.UNKNOWN;
  }
}

function cardValue(preview, t) {
  switch (preview.status) {
    case PREVIEW_LIMIT_STATUS.ERROR:
    case PREVIEW_LIMIT_STATUS.UNCHECKED:
    case PREVIEW_LIMIT_STATUS.LOADING:
      return '—';
    case PREVIEW_LIMIT_STATUS.UNLIMITED:
    case PREVIEW_LIMIT_STATUS.NONE:
      return t('CAMPAIGN.WHATSAPP_OFFICIAL.PREVIEW_LIMIT.UNLIMITED');
    default:
      return preview.limit.toLocaleString('pt-BR');
  }
}

function cardSubtitle(preview, t) {
  switch (preview.status) {
    case PREVIEW_LIMIT_STATUS.ERROR:
      return t('CAMPAIGN.WHATSAPP_OFFICIAL.PREVIEW_LIMIT.ERROR');
    case PREVIEW_LIMIT_STATUS.UNCHECKED:
      return t('CAMPAIGN.WHATSAPP_OFFICIAL.PREVIEW_LIMIT.UNCHECKED');
    case PREVIEW_LIMIT_STATUS.LOADING:
      return t('CAMPAIGN.WHATSAPP_OFFICIAL.PREVIEW_LIMIT.LOADING');
    case PREVIEW_LIMIT_STATUS.REACHED:
    case PREVIEW_LIMIT_STATUS.EXCEEDS:
      return cutText(preview, t);
    case PREVIEW_LIMIT_STATUS.CAPPED:
      return preview.cut
        ? `${cappedText(preview, t)} · ${cutText(preview, t)}`
        : cappedText(preview, t);
    case PREVIEW_LIMIT_STATUS.UNLIMITED:
      return t('CAMPAIGN.WHATSAPP_OFFICIAL.PREVIEW_LIMIT.UNLIMITED_TIER');
    case PREVIEW_LIMIT_STATUS.NONE:
      return t('CAMPAIGN.WHATSAPP_OFFICIAL.PREVIEW_LIMIT.NONE');
    default:
      return t('CAMPAIGN.WHATSAPP_OFFICIAL.PREVIEW_LIMIT.WITHIN', {
        rating: preview.qualityRating,
      });
  }
}

// Tooltip do card; null = sem tooltip.
function cardHint(preview, t) {
  if (preview.status !== PREVIEW_LIMIT_STATUS.CAPPED) return null;
  return t('CAMPAIGN.WHATSAPP_OFFICIAL.PREVIEW_LIMIT.CAPPED_HINT', {
    limit: preview.limit.toLocaleString('pt-BR'),
  });
}

// `t` entra por parâmetro: o módulo não conhece o i18n, o que o deixa testável
// sem montar o app.
export function previewLimitCard(preview, t) {
  const ink = cardInk(preview);
  return {
    status: preview.status,
    icon: ink.icon,
    title: t('CAMPAIGN.WHATSAPP_OFFICIAL.PREVIEW_LIMIT.TITLE'),
    value: cardValue(preview, t),
    subtitle: cardSubtitle(preview, t),
    hint: cardHint(preview, t),
    bgColor: ink.bg,
    textColor: ink.text,
    iconColor: ink.text,
  };
}

// Frase do cabeçalho das telas de configuração (PageHeader, via
// useComputedProperties#headerContent). Sai do MESMO `buildPreviewLimit` do
// card: antes o cabeçalho repetia o `message` do backend sem olhar loading nem
// a lista, e havia um 2º cabeçalho (`tableHeaderContent`, removido) com regra
// própria sobre o `limit` cru ("permite até 1.000/dia" com canal não conferido).
export function previewLimitHeader(preview, t) {
  switch (preview.status) {
    case PREVIEW_LIMIT_STATUS.OK:
    case PREVIEW_LIMIT_STATUS.EXCEEDS:
    case PREVIEW_LIMIT_STATUS.REACHED: {
      const daily = t('CAMPAIGN.WHATSAPP_OFFICIAL.PREVIEW_LIMIT.HEADER.DAILY', {
        limit: preview.limit.toLocaleString('pt-BR'),
        remaining: preview.remainingToday.toLocaleString('pt-BR'),
      });
      return preview.cut ? `${daily} ${cutText(preview, t)}.` : daily;
    }
    case PREVIEW_LIMIT_STATUS.CAPPED: {
      const capped = t(
        'CAMPAIGN.WHATSAPP_OFFICIAL.PREVIEW_LIMIT.HEADER.CAPPED',
        {
          limit: preview.limit.toLocaleString('pt-BR'),
          remaining: preview.remainingToday.toLocaleString('pt-BR'),
        }
      );
      return preview.cut ? `${capped} ${cutText(preview, t)}.` : capped;
    }
    default:
      return cardSubtitle(preview, t);
  }
}
